/**
 * Face framing — is there a face, and is it inside the guide oval?
 *
 * This is a *framing* check, not identity or liveness: it answers "will this photo be
 * usable" before the shutter is pressed, so the user is not sent to the review screen with
 * a picture of their forehead. The real verification still happens on the backend.
 *
 * Detection is MediaPipe's BlazeFace short-range model, run through
 * `@mediapipe/tasks-vision`. It returns a face box with a confidence score, and nothing at
 * all for a wall — which is the whole reason to carry a model rather than a colour rule.
 *
 * Everything runs on the device. The runtime, the model and the app are served from the
 * same origin (see `vue.config.js`); no request leaves for Google or any CDN, at build
 * time or at run time.
 *
 * Everything the rest of the app sees is in **display coordinates**: the frame is cropped
 * the way `object-fit: cover` crops it and mirrored the way the preview is mirrored, so a
 * box from here compares directly against the on-screen guide oval.
 *
 * The runtime needs WebGL, even on the CPU delegate. When it cannot load there is no second
 * detector to fall back to, so the camera screens hand the shutter back rather than trap
 * the user; see their `armManualFallback`.
 */

/** Where the runtime, and the model, are served from. Same origin, always. */
const RUNTIME_PATH = "/mediapipe";
const MODEL_PATH = "/models/blaze_face_short_range.tflite";

/**
 * Below this the detector is not confident enough to gate on.
 *
 * Higher than the model's own 0.5 default: acting on a weak detection means unlocking the
 * shutter for something that may not be a face, and the cost of waiting one more frame is
 * nothing.
 */
const MIN_SCORE = 0.6;

/** Longest wait for the runtime before the screen gives up on it. */
const LOAD_TIMEOUT_MS = 20000;

/** Analysis width in px. The model works from a small square; a wider frame buys nothing. */
const ANALYSIS_WIDTH = 256;

let runtimePromise = null;

/**
 * How many trackers are currently using the shared task.
 *
 * Reference counted rather than closed per screen: the runtime is ~12 MB of WebAssembly,
 * and the flow moves between two screens that both need it, with retakes in between. It is
 * built once and closed when the last screen lets go.
 */
let holders = 0;

/** Give the shared task back; the last one out closes it. */
function release() {
  holders = Math.max(0, holders - 1);
  if (holders > 0 || !runtimePromise) return;
  const pending = runtimePromise;
  runtimePromise = null;
  // The task may still be loading; close it once it exists rather than orphaning it.
  pending.then(
    (detector) => {
      if (detector && detector.close) {
        try {
          detector.close();
        } catch (e) {
          /* closing twice is not worth reporting */
        }
      }
    },
    () => {}
  );
}

/**
 * Load a classic script once, resolving when it has run.
 *
 * A tag whose fetch already failed is removed rather than listened to: `load` and `error`
 * fire once, so attaching to a dead tag produces a promise that never settles — the retry
 * the caller thinks it is making would hang instead of failing.
 */
function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) return resolve();
      if (!existing.dataset.failed) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () => reject(new Error("script failed")));
        return;
      }
      existing.parentNode.removeChild(existing);
    }

    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "1";
      resolve();
    };
    script.onerror = () => {
      script.dataset.failed = "1";
      reject(new Error("script failed"));
    };
    document.head.appendChild(script);
  });
}

/**
 * Load the MediaPipe runtime and build a detector.
 *
 * Deliberately the IIFE bundle behind a `<script>` tag rather than an `import`: the package
 * is ESM, this app builds on webpack 4, and dragging the bundler into it would be a much
 * larger change than the feature deserves. The bundle publishes a `Vision` global.
 *
 * Cached as a promise and reference counted: screens that overlap share one task, and the
 * last one to let go closes it. What is never paid twice is the download — the script tag
 * and the WebAssembly stay in the browser's cache — so a later screen rebuilds the task in
 * tens of milliseconds rather than re-fetching megabytes.
 */
export function loadFaceDetector() {
  if (runtimePromise) return runtimePromise;

  runtimePromise = (async () => {
    await loadScript(`${RUNTIME_PATH}/vision_bundle.js`);
    const Vision = window.Vision;
    if (!Vision || !Vision.FilesetResolver) throw new Error("mediapipe bundle missing");

    // Picks the SIMD or the no-SIMD runtime by probing what the browser supports.
    const fileset = await Vision.FilesetResolver.forVisionTasks(`${RUNTIME_PATH}/wasm`);
    return Vision.FaceDetector.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: MODEL_PATH,
        // CPU rather than GPU: the model is tiny, this measured *faster* than the GPU
        // delegate, and it avoids handing a phone's GPU a job it may throttle. WebGL is
        // still required either way — the task runner uses it to move frames around.
        delegate: "CPU",
      },
      runningMode: "VIDEO",
      minDetectionConfidence: 0.5,
    });
  })();

  // A failed load must not be cached as a permanent "broken": a later screen deserves a
  // fresh attempt (the user may have regained connectivity).
  runtimePromise.catch(() => {
    runtimePromise = null;
  });

  return runtimePromise;
}

/** Reject a promise that takes too long, without cancelling it. */
function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("timeout")), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

/**
 * The source rectangle `object-fit: cover` would show for a box of the given aspect ratio.
 * Without this the detector would run on parts of the frame the user cannot see, and a
 * face just outside the visible crop would count as framed.
 */
function coverCrop(videoWidth, videoHeight, aspect) {
  const sourceAspect = videoWidth / videoHeight;
  let sw = videoWidth;
  let sh = videoHeight;
  if (sourceAspect > aspect) sw = videoHeight * aspect;
  else sh = videoWidth / aspect;
  return { sx: (videoWidth - sw) / 2, sy: (videoHeight - sh) / 2, sw, sh };
}

/**
 * A detector bound to one canvas, holding the MediaPipe task for the life of a screen.
 */
export function createFaceTracker() {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  let detector = null;
  let disposed = false;
  let retained = false;
  let lastStamp = 0;

  function retain() {
    if (retained) return;
    retained = true;
    holders += 1;
  }

  function releaseFaceDetector() {
    if (!retained) return;
    retained = false;
    release();
  }

  return {
    /** Resolves once detection is possible; rejects if the runtime cannot be had. */
    async ready() {
      if (detector) return detector;
      retain();
      const loaded = await withTimeout(loadFaceDetector(), LOAD_TIMEOUT_MS);
      // Disposed while the load was in flight: hand the reference straight back, or the
      // task stays alive with nobody holding it.
      if (disposed) {
        releaseFaceDetector();
        return null;
      }
      detector = loaded;
      return detector;
    },

    /**
     * Locate the face in the frame currently on screen.
     *
     * @param {HTMLVideoElement} video the live preview
     * @param {number} aspect width / height of the on-screen frame
     * @returns {Promise<{x,y,width,height,score}|null>} normalised to the visible frame and
     *   mirrored like the preview; null when no face was found.
     */
    async detect(video, aspect) {
      if (!detector || !video || !video.videoWidth || !aspect) return null;

      const height = Math.round(ANALYSIS_WIDTH / aspect);
      if (canvas.width !== ANALYSIS_WIDTH || canvas.height !== height) {
        canvas.width = ANALYSIS_WIDTH;
        canvas.height = height;
      }

      const crop = coverCrop(video.videoWidth, video.videoHeight, aspect);
      ctx.save();
      // Same mirror as the preview, so "left" here means the user's left on screen.
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, canvas.width, canvas.height);
      ctx.restore();

      // VIDEO mode wants a strictly increasing timestamp; the clock is only used to order
      // frames, so its origin does not matter.
      lastStamp += 1;
      let result;
      try {
        result = detector.detectForVideo(canvas, lastStamp);
      } catch (e) {
        // A single bad frame (context lost, canvas resized mid-flight) is not a verdict.
        return null;
      }

      const detections = (result && result.detections) || [];
      let best = null;
      for (const detection of detections) {
        const score =
          detection.categories && detection.categories.length ? detection.categories[0].score : 0;
        const box = detection.boundingBox;
        if (!box || score < MIN_SCORE) continue;
        // Someone in the background must never win over the person holding the phone.
        if (!best || box.width * box.height > best.width * best.height) {
          best = { ...box, score };
        }
      }
      // QA hook: `window.__faceDebug = true` publishes the raw detection, which is how the
      // zone below gets calibrated against a real camera rather than by eye.
      if (window.__faceDebug) {
        window.__faceLast = best
          ? {
              score: +best.score.toFixed(3),
              box: [
                +(best.originX / canvas.width).toFixed(3),
                +(best.originY / canvas.height).toFixed(3),
                +(best.width / canvas.width).toFixed(3),
                +(best.height / canvas.height).toFixed(3),
              ],
            }
          : { score: 0, box: null, seen: detections.length };
      }

      if (!best) return null;

      return {
        x: best.originX / canvas.width,
        y: best.originY / canvas.height,
        width: best.width / canvas.width,
        height: best.height / canvas.height,
        score: best.score,
      };
    },

    /**
     * Release this tracker's hold on the runtime.
     *
     * The MediaPipe task itself is deliberately *not* closed. It is shared — one task
     * serves the photo step, the retake, and the video step — and closing it here meant
     * rebuilding the whole WebAssembly runtime on every one of those transitions, which
     * is a visible stall on a phone. It is closed once, when the last tracker lets go.
     */
    dispose() {
      if (disposed) return;
      disposed = true;
      canvas.width = 0;
      canvas.height = 0;
      detector = null;
      releaseFaceDetector();
    },
  };
}

/**
 * Where a face sits inside the guide oval.
 *
 * The oval is drawn to hold a whole *head*, but the model reports the *face*: a square from
 * brow to chin, no hair and no headscarf. So a correctly positioned face is shorter than
 * the oval and sits above its centre. Measured against the repo's own "correct photo"
 * sample (`public/files/True01.jpg`).
 */
const FACE_ZONE_CENTRE_Y = 0.44;
const FACE_ZONE_HEIGHT = 0.62;

/** Too small / too large relative to the face zone, as a multiple of its height. */
const TOO_FAR = 0.68;
const TOO_CLOSE = 1.4;

/** Off-centre threshold, as a share of the guide oval's own width/height. */
const OFF_CENTRE_X = 0.22;
const OFF_CENTRE_Y = 0.26;

/** Shown once the framing has settled — see the streak logic in `createFramingWatch`. */
export const FRAMED_HINT = "چهره در کادر است";

/**
 * Shown for a good frame that has not held long enough yet.
 *
 * Without it the chip announced success on the first good frame while the button stayed
 * disabled for another three — the screen telling the user to act on a control that does
 * not work yet.
 */
export const HOLDING_HINT = "همین‌طور بمانید…";

const HINTS = {
  searching: "صورت خود را داخل کادر قرار دهید",
  far: "کمی نزدیک‌تر شوید",
  close: "کمی عقب‌تر بروید",
  right: "کمی به راست بروید",
  left: "کمی به چپ بروید",
  down: "کمی پایین‌تر بیایید",
  up: "کمی بالاتر بیایید",
  ok: FRAMED_HINT,
};

/**
 * Compare a face box against the guide oval.
 *
 * One correction at a time, worst first: a list of three things to fix at once is a list
 * nobody follows. Directions are in the user's own frame — the preview is mirrored, so it
 * behaves like a mirror and "به راست" moves the face right on screen.
 *
 * @param {{x,y,width,height}|null} face normalised, mirrored (see `createFaceTracker`)
 * @param {{x,y,width,height}} target the guide oval, in the same coordinates
 * @returns {{status:"searching"|"adjust"|"ok", reason:string, hint:string}}
 */
export function classifyFraming(face, target) {
  if (!face || !target || !target.width || !target.height) {
    return { status: "searching", reason: "searching", hint: HINTS.searching };
  }

  const ratio = face.height / (target.height * FACE_ZONE_HEIGHT);
  if (ratio < TOO_FAR) return { status: "adjust", reason: "far", hint: HINTS.far };
  if (ratio > TOO_CLOSE) return { status: "adjust", reason: "close", hint: HINTS.close };

  const zoneCentreY = target.y + target.height * FACE_ZONE_CENTRE_Y;
  const dx = (face.x + face.width / 2 - (target.x + target.width / 2)) / target.width;
  const dy = (face.y + face.height / 2 - zoneCentreY) / target.height;

  const offX = Math.abs(dx) / OFF_CENTRE_X;
  const offY = Math.abs(dy) / OFF_CENTRE_Y;

  if (offX > 1 && offX >= offY) {
    // Face left of the oval means the user has to move right to bring it back.
    const reason = dx < 0 ? "right" : "left";
    return { status: "adjust", reason, hint: HINTS[reason] };
  }
  if (offY > 1) {
    const reason = dy < 0 ? "down" : "up";
    return { status: "adjust", reason, hint: HINTS[reason] };
  }

  return { status: "ok", reason: "ok", hint: HINTS.ok };
}

/** Analysis cadence. ~8 fps: fast enough to feel live, far below what a frame costs. */
const TICK_MS = 120;

/** Consecutive good frames before framing counts as settled, and bad ones before it stops. */
const OK_FRAMES = 4;
const LOST_FRAMES = 3;

/**
 * Run the framing check over a live preview and report when the verdict changes.
 *
 * Both media screens need exactly this loop, so it lives here rather than in a component:
 * the photo step gates its shutter on it, and the video step gates the start of recording.
 *
 * Streaks in both directions. A face counts as framed only after it holds for a few frames,
 * and stops counting only once it has genuinely left — reacting to single frames makes the
 * ring strobe and the button flicker between enabled and disabled.
 *
 * @param {object} options
 * @param {() => HTMLVideoElement} options.getVideo the live preview
 * @param {() => ({target:object, aspect:number}|null)} options.getGeometry where the guide is
 * @param {(state:{status:string, reason:string, hint:string, framed:boolean}) => void}
 *   options.onChange
 * @param {(error:Error) => void} [options.onUnavailable] the detector could not be loaded
 * @returns {{start:Function, stop:Function}}
 */
export function createFramingWatch({ getVideo, getGeometry, onChange, onUnavailable }) {
  let tracker = null;
  let timer = null;
  let okStreak = 0;
  let lostStreak = 0;
  let framed = false;
  let status = "searching";
  let ticking = false;

  /** Start the loop at most once, however many things ask for it. */
  function beginTicking() {
    if (ticking || !tracker) return;
    ticking = true;
    tick();
  }

  function report(nextStatus, reason, hint) {
    status = nextStatus;
    onChange({ status: nextStatus, reason, hint, framed });
  }

  async function tick() {
    timer = null;
    if (!tracker) return;

    let verdict = framingOverride();
    if (!verdict) {
      const geometry = getGeometry();
      const face = geometry ? await tracker.detect(getVideo(), geometry.aspect) : null;
      verdict = classifyFraming(face, geometry && geometry.target);
    }

    // The await above can outlive the screen.
    if (!tracker) return;

    if (verdict.status === "ok") {
      okStreak++;
      lostStreak = 0;
    } else {
      lostStreak++;
      okStreak = 0;
    }
    framed = framed ? lostStreak < LOST_FRAMES : okStreak >= OK_FRAMES;

    // Three cases, and the middle one is the one that used to lie: framed (settled),
    // good-but-not-settled (hold still), and an actual correction. While framed, a stray
    // bad frame must not flash an instruction the user is not supposed to act on.
    let hint = verdict.hint;
    if (framed) hint = FRAMED_HINT;
    else if (verdict.status === "ok") hint = HOLDING_HINT;
    report(verdict.status, verdict.reason, hint);

    timer = setTimeout(tick, TICK_MS);
  }

  return {
    /** A timeout chain, not an interval: a slow frame delays the next one, never stacks. */
    start() {
      if (tracker) return;
      tracker = createFaceTracker();
      okStreak = 0;
      lostStreak = 0;
      framed = false;
      report("searching", "searching", HINTS.searching);

      // A demo or a test driving the verdict should not have to wait on 12 MB of
      // WebAssembly to say what it has already decided — but the runtime is still loaded
      // underneath, so clearing the override mid-run recovers into real detection instead
      // of leaving the loop looking at nothing.
      if (framingOverride()) beginTicking();

      tracker.ready().then(
        (ready) => {
          if (tracker && ready) beginTicking();
        },
        (error) => {
          if (!tracker || framingOverride()) return;
          // Nothing is going to detect anything on this device. Say so once and stop; the
          // screen turns the guidance into advice and gives the button back.
          if (onUnavailable) onUnavailable(error);
        }
      );
    },

    stop() {
      clearTimeout(timer);
      timer = null;
      ticking = false;
      if (tracker) {
        tracker.dispose();
        tracker = null;
      }
      okStreak = 0;
      lostStreak = 0;
      if (framed || status !== "searching") {
        framed = false;
        report("searching", "searching", "");
      }
    },
  };
}

/** Demo/QA override: `window.__faceFraming = "ok" | "adjust" | "searching"`. */
export function framingOverride() {
  if (typeof window === "undefined") return null;
  const forced = window.__faceFraming;
  if (forced !== "ok" && forced !== "adjust" && forced !== "searching") return null;
  return { status: forced, reason: forced, hint: HINTS[forced === "adjust" ? "far" : forced] };
}
